import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  getDashboardStats, 
  getDeveloperCode, 
  getAllMaintenance, 
  createMaintenance,
  updateMaintenance,
  deleteMaintenance,
  getAllUsers,
  sendMaintenanceNotification  // ✅ IMPORTANT: Add this import
} from '../../services/api';
import './AdminDashboard.css';

const AdminDashboard = ({ user, onLogout }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [maintenanceList, setMaintenanceList] = useState([]);
  const [showMaintenanceForm, setShowMaintenanceForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [filter, setFilter] = useState('all');
  const [users, setUsers] = useState([]);
  const [notificationLoading, setNotificationLoading] = useState({}); // For individual notify buttons
  const navigate = useNavigate();

  useEffect(() => {
    fetchDashboardData();
    fetchMaintenanceData();
    fetchUsersData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await getDashboardStats();
      if (response.success) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMaintenanceData = async () => {
    try {
      const response = await getAllMaintenance();
      if (response.success) {
        setMaintenanceList(response.maintenance);
      }
    } catch (error) {
      console.error('Error fetching maintenance:', error);
    }
  };

  const fetchUsersData = async () => {
    try {
      const response = await getAllUsers();
      if (response.success) {
        setUsers(response.users || []);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  // ✅ Handle manual notification sending
  const handleSendNotification = async (maintenanceId) => {
    if (!window.confirm('Send maintenance notification email to all users?')) {
      return;
    }

    setNotificationLoading(prev => ({ ...prev, [maintenanceId]: true }));

    try {
      const response = await sendMaintenanceNotification(maintenanceId);
      if (response.success) {
        alert(`✅ Notification emails sent successfully!\n\n${response.emailResult?.message || `${response.emailResult?.success || 0} emails sent.`}`);
      }
    } catch (error) {
      alert('❌ Error sending notifications');
      console.error('Notification error:', error);
    } finally {
      setNotificationLoading(prev => ({ ...prev, [maintenanceId]: false }));
    }
  };

  const handleDeleteMaintenance = async (id) => {
    if (!window.confirm('Are you sure you want to delete this maintenance schedule?')) {
      return;
    }

    try {
      const response = await deleteMaintenance(id);
      if (response.success) {
        alert('✅ Maintenance deleted successfully!');
        fetchMaintenanceData();
      }
    } catch (error) {
      alert('❌ Error deleting maintenance');
    }
  };

  const handleEditMaintenance = (maintenance) => {
    setEditingId(maintenance._id);
    setShowMaintenanceForm(true);
  };

  const filteredMaintenance = maintenanceList.filter(item => {
    const now = new Date();
    const endTime = new Date(item.endTime);
    
    if (filter === 'all') return true;
    if (filter === 'scheduled') return new Date(item.startTime) > now;
    if (filter === 'active') return item.status === 'ongoing';
    if (filter === 'completed') return endTime < now || item.status === 'completed';
    return true;
  });

  const totalUsers = users.length;
  const activeUsers = users.filter(user => user.subscription?.hasActiveAccess).length;
  const trialUsers = users.filter(user => user.subscription?.plan === 'trial').length;
  const paidUsers = users.filter(user => user.subscription?.plan === 'paid').length;

  return (
    <div className="admin-dashboard-container">
      {/* Dashboard Header */}
      <div className="dashboard-main-header">
        <div className="dashboard-header-left">
          <h1 className="dashboard-page-title">Dashboard Overview</h1>
          <div className="dashboard-server-status">
            <span className="status-indicator online"></span>
            <span className="status-text">System Online</span>
          </div>
        </div>
        
        <div className="dashboard-header-right">
          <div className="dashboard-user-info">
            <div className="dashboard-user-avatar">
              {user?.email?.charAt(0).toUpperCase() || 'A'}
            </div>
            <div className="dashboard-user-details">
              <div className="dashboard-user-name">{user?.name || user?.email || 'Admin'}</div>
              <div className="dashboard-user-role">{user?.role || 'Administrator'}</div>
            </div>
          </div>
          <button onClick={onLogout} className="dashboard-logout-btn">
            <span className="dashboard-logout-icon">🚪</span>
            Logout
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="dashboard-stats-grid">
        <div className="dashboard-stat-card">
          <div className="dashboard-stat-icon users">👥</div>
          <div className="dashboard-stat-content">
            <h3 className="dashboard-stat-title">Total Users</h3>
            <p className="dashboard-stat-number">{loading ? '...' : totalUsers}</p>
            <div className="dashboard-stat-trend positive">
              +{Math.floor(totalUsers * 0.12 || 0)} this month
            </div>
          </div>
        </div>

        <div className="dashboard-stat-card">
          <div className="dashboard-stat-icon active">🟢</div>
          <div className="dashboard-stat-content">
            <h3 className="dashboard-stat-title">Active Users</h3>
            <p className="dashboard-stat-number">{activeUsers}</p>
            <div className="dashboard-stat-trend positive">
              +{Math.floor(activeUsers * 0.08 || 0)} today
            </div>
          </div>
        </div>

        <div className="dashboard-stat-card">
          <div className="dashboard-stat-icon trial">🆓</div>
          <div className="dashboard-stat-content">
            <h3 className="dashboard-stat-title">Trial Users</h3>
            <p className="dashboard-stat-number">{trialUsers}</p>
            <div className="dashboard-stat-trend neutral">
              Active trials
            </div>
          </div>
        </div>

        <div className="dashboard-stat-card">
          <div className="dashboard-stat-icon paid">💰</div>
          <div className="dashboard-stat-content">
            <h3 className="dashboard-stat-title">Paid Users</h3>
            <p className="dashboard-stat-number">{paidUsers}</p>
            <div className="dashboard-stat-trend positive">
              +{Math.floor(paidUsers * 0.15 || 0)} this quarter
            </div>
          </div>
        </div>
      </div>

      {/* Developer Section */}
      {user?.isDeveloper && (
        <div className="dashboard-dev-section">
          <div className="dashboard-section-header">
            <h2 className="dashboard-section-title">
              <span className="dashboard-section-icon">🔧</span> Developer Tools
            </h2>
            <div className="dashboard-dev-tag">Advanced Access</div>
          </div>
          <div className="dashboard-dev-buttons">
            <DeveloperCodeButton />
            <button className="dashboard-dev-button">
              <span className="dashboard-button-icon">🗄️</span> Database
            </button>
            <button className="dashboard-dev-button">
              <span className="dashboard-button-icon">⚙️</span> Settings
            </button>
            <button className="dashboard-dev-button">
              <span className="dashboard-button-icon">📋</span> Logs
            </button>
          </div>
        </div>
      )}

      {/* Maintenance Management Section */}
      <div className="dashboard-maintenance-section">
        <div className="dashboard-section-header">
          <div>
            <h2 className="dashboard-section-title">
              <span className="dashboard-section-icon">🛠️</span> Maintenance Management
            </h2>
            <p className="dashboard-section-subtitle">
              Schedule and manage system maintenance windows
            </p>
          </div>
          
          <button 
            onClick={() => {
              setEditingId(null);
              setShowMaintenanceForm(!showMaintenanceForm);
            }}
            className="dashboard-toggle-btn"
          >
            <span className="dashboard-button-icon">
              {showMaintenanceForm ? '✕' : '+'}
            </span>
            {showMaintenanceForm ? 'Cancel' : 'Schedule New'}
          </button>
        </div>

        {/* Filter Buttons */}
        <div className="dashboard-filter-container">
          <div className="dashboard-filter-buttons">
            {['all', 'scheduled', 'active', 'completed'].map((filterType) => (
              <button
                key={filterType}
                onClick={() => setFilter(filterType)}
                className={`dashboard-filter-btn ${filter === filterType ? 'active' : ''}`}
              >
                {filterType.charAt(0).toUpperCase() + filterType.slice(1)}
                <span className="dashboard-filter-count">
                  ({maintenanceList.filter(item => {
                    const now = new Date();
                    const endTime = new Date(item.endTime);
                    if (filterType === 'all') return true;
                    if (filterType === 'scheduled') return new Date(item.startTime) > now;
                    if (filterType === 'active') return item.status === 'ongoing';
                    if (filterType === 'completed') return endTime < now || item.status === 'completed';
                    return true;
                  }).length})
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Maintenance Form */}
        {showMaintenanceForm && (
          <MaintenanceForm 
            editingId={editingId}
            onSuccess={() => {
              setShowMaintenanceForm(false);
              setEditingId(null);
              fetchMaintenanceData();
            }}
            onCancel={() => {
              setShowMaintenanceForm(false);
              setEditingId(null);
            }}
          />
        )}

        {/* Maintenance List */}
        <div className="dashboard-maintenance-list">
          <h3 className="dashboard-list-title">
            Maintenance Windows ({filteredMaintenance.length})
          </h3>
          
          {filteredMaintenance.length === 0 ? (
            <div className="dashboard-empty-state">
              <div className="dashboard-empty-icon">📅</div>
              <p className="dashboard-empty-text">No maintenance scheduled</p>
              <button 
                onClick={() => setShowMaintenanceForm(true)}
                className="dashboard-empty-button"
              >
                Schedule Your First Maintenance
              </button>
            </div>
          ) : (
            <div className="dashboard-maintenance-grid">
              {filteredMaintenance.map(item => (
                <MaintenanceItem 
                  key={item._id} 
                  item={item}
                  onEdit={handleEditMaintenance}
                  onDelete={handleDeleteMaintenance}
                  onNotify={handleSendNotification}
                  isNotifying={notificationLoading[item._id] || false}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Recent Activity */}
      <div className="dashboard-recent-activity">
        <div className="dashboard-section-header">
          <h2 className="dashboard-section-title">
            <span className="dashboard-section-icon">📈</span> Recent Activity
          </h2>
          <button 
            onClick={() => navigate('/admin/users')}
            className="dashboard-view-all"
          >
            View All Users
          </button>
        </div>
        <div className="dashboard-activity-list">
          {users.slice(0, 5).map(userItem => (
            <div key={userItem._id} className="dashboard-activity-item">
              <div className="dashboard-activity-avatar">
                {(userItem.name || userItem.email || 'U').charAt(0).toUpperCase()}
              </div>
              <div className="dashboard-activity-content">
                <div className="dashboard-activity-title">
                  <span className="dashboard-user-name">{userItem.name || userItem.email || 'Unknown User'}</span>
                  <span className={`dashboard-user-plan ${userItem.subscription?.plan || 'unknown'}`}>
                    {(userItem.subscription?.plan || 'unknown').toUpperCase()}
                  </span>
                </div>
                <div className="dashboard-activity-details">
                  <span className="dashboard-detail-item">
                    <span className="dashboard-detail-icon">🏢</span>
                    {userItem.company || 'No company'}
                  </span>
                  <span className="dashboard-detail-item">
                    <span className="dashboard-detail-icon">📧</span>
                    {userItem.email || 'No email'}
                  </span>
                  <span className="dashboard-detail-item">
                    <span className="dashboard-detail-icon">📅</span>
                    {userItem.createdAt ? new Date(userItem.createdAt).toLocaleDateString() : 'Unknown'}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const DeveloperCodeButton = () => {
  const [loading, setLoading] = useState(false);

  const handleClick = async () => {
    setLoading(true);
    try {
      const response = await getDeveloperCode();
      if (response.success) {
        alert('✅ Developer access granted!\n\nCode: ' + 
              JSON.stringify(response.data, null, 2));
      } else {
        alert('❌ ' + response.message);
      }
    } catch (error) {
      alert('❌ Error accessing code');
    } finally {
      setLoading(false);
    }
  };

  return (
    <button 
      onClick={handleClick} 
      className="dashboard-dev-button"
      disabled={loading}
    >
      <span className="dashboard-button-icon">
        {loading ? '⏳' : '👁️'}
      </span>
      {loading ? 'Loading...' : 'View Code'}
    </button>
  );
};

const MaintenanceItem = ({ item, onEdit, onDelete, onNotify, isNotifying }) => {
  const now = new Date();
  const startTime = new Date(item.startTime);
  const endTime = new Date(item.endTime);
  const isPast = endTime < now;
  const isActive = item.status === 'ongoing';
  const isScheduled = startTime > now;

  const getStatusColor = () => {
    if (isActive) return '#ff4d4f';
    if (isScheduled) return '#1890ff';
    if (isPast) return '#52c41a';
    return '#faad14';
  };

  const getStatusText = () => {
    if (isActive) return 'ACTIVE';
    if (isScheduled) return 'SCHEDULED';
    if (isPast) return 'COMPLETED';
    return item.status.toUpperCase();
  };

  return (
    <div className="dashboard-maintenance-item" style={{ borderLeftColor: getStatusColor() }}>
      <div className="dashboard-item-header">
        <div>
          <strong className="dashboard-item-title">{item.title}</strong>
          <div className="dashboard-status-badge" style={{ backgroundColor: getStatusColor() }}>
            {getStatusText()}
          </div>
        </div>
        <div className="dashboard-item-actions">
          {/* ✅ Notify Button - Only show for scheduled/active maintenance */}
          {!isPast && (
            <button 
              onClick={() => onNotify(item._id)}
              className="dashboard-notify-btn"
              disabled={isNotifying}
              title="Send email notification to all users"
            >
              <span className="dashboard-action-icon">
                {isNotifying ? '⏳' : '📧'}
              </span>
              {isNotifying ? 'Sending...' : 'Notify'}
            </button>
          )}
          
          {!isPast && (
            <button 
              onClick={() => onEdit(item)}
              className="dashboard-edit-btn"
            >
              <span className="dashboard-action-icon">✏️</span> Edit
            </button>
          )}
          <button 
            onClick={() => onDelete(item._id)}
            className="dashboard-delete-btn"
          >
            <span className="dashboard-action-icon">🗑️</span> Delete
          </button>
        </div>
      </div>
      
      <p className="dashboard-item-description">{item.description}</p>
      
      <div className="dashboard-item-details">
        <div className="dashboard-detail-item">
          <span className="dashboard-detail-label">Start:</span>
          <span className="dashboard-detail-value">
            {startTime.toLocaleString('en-IN', {
              dateStyle: 'medium',
              timeStyle: 'short'
            })}
          </span>
        </div>
        <div className="dashboard-detail-item">
          <span className="dashboard-detail-label">End:</span>
          <span className="dashboard-detail-value">
            {endTime.toLocaleString('en-IN', {
              dateStyle: 'medium',
              timeStyle: 'short'
            })}
          </span>
        </div>
        <div className="dashboard-detail-item">
          <span className="dashboard-detail-label">Created by:</span>
          <span className="dashboard-detail-value">
            {item.createdBy || 'admin'}
          </span>
        </div>
      </div>

      {isActive && (
        <div className="dashboard-active-warning">
          <span className="dashboard-warning-icon">⚠️</span>
          Site is currently under maintenance. Users cannot access the system.
        </div>
      )}
    </div>
  );
};

const MaintenanceForm = ({ editingId, onSuccess, onCancel }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    startTime: '',
    endTime: '',
    notifyUsers: editingId ? false : true  // ✅ Default: true for new, false for edit
  });
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (editingId) {
      fetchMaintenanceDetails();
    }
  }, [editingId]);

  const fetchMaintenanceDetails = async () => {
    setLoading(true);
    try {
      const response = await getAllMaintenance();
      if (response.success) {
        const maintenance = response.maintenance.find(m => m._id === editingId);
        if (maintenance) {
          setFormData({
            title: maintenance.title,
            description: maintenance.description,
            startTime: new Date(maintenance.startTime).toISOString().slice(0, 16),
            endTime: new Date(maintenance.endTime).toISOString().slice(0, 16),
            notifyUsers: false // Default to false when editing
          });
        }
      }
    } catch (error) {
      console.error('Error fetching maintenance details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const start = new Date(formData.startTime);
      const end = new Date(formData.endTime);
      const now = new Date();

      if (start < now && !editingId) {
        alert('Start time cannot be in the past for new schedules');
        return;
      }

      if (end <= start) {
        alert('End time must be after start time');
        return;
      }

      let response;
      if (editingId) {
        response = await updateMaintenance(editingId, {
          title: formData.title,
          description: formData.description,
          startTime: formData.startTime,
          endTime: formData.endTime,
          notifyUsers: formData.notifyUsers // ✅ Send notification preference
        });
      } else {
        response = await createMaintenance({
          title: formData.title,
          description: formData.description,
          startTime: formData.startTime,
          endTime: formData.endTime,
          notifyUsers: formData.notifyUsers // ✅ Send notification preference
        });
      }

      if (response.success) {
        let message = `✅ Maintenance ${editingId ? 'updated' : 'scheduled'} successfully!`;
        
        // Show email notification status
        if (formData.notifyUsers) {
          message += '\n📧 Email notifications are being sent to all users.';
        }
        
        alert(message);
        onSuccess();
      }
    } catch (error) {
      alert(`❌ Error ${editingId ? 'updating' : 'scheduling'} maintenance: ${error.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="dashboard-loading-form">
        <div className="dashboard-loading-spinner"></div>
        Loading maintenance details...
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="dashboard-maintenance-form">
      <div className="dashboard-form-header">
        <h4 className="dashboard-form-title">
          {editingId ? '✏️ Edit Maintenance' : '➕ Schedule New Maintenance'}
        </h4>
      </div>
      
      <div className="dashboard-form-group">
        <label className="dashboard-form-label">Title *</label>
        <input
          type="text"
          placeholder="e.g., Database Maintenance, System Update"
          value={formData.title}
          onChange={(e) => setFormData({...formData, title: e.target.value})}
          required
          className="dashboard-form-input"
        />
      </div>
      
      <div className="dashboard-form-group">
        <label className="dashboard-form-label">Description *</label>
        <textarea
          placeholder="Describe what will be done during maintenance..."
          value={formData.description}
          onChange={(e) => setFormData({...formData, description: e.target.value})}
          required
          className="dashboard-form-textarea"
          rows={4}
        />
      </div>
      
      <div className="dashboard-date-group">
        <div className="dashboard-date-input">
          <label className="dashboard-form-label">Start Time *</label>
          <input
            type="datetime-local"
            value={formData.startTime}
            onChange={(e) => setFormData({...formData, startTime: e.target.value})}
            required
            className="dashboard-form-input"
            min={new Date().toISOString().slice(0, 16)}
          />
        </div>
        
        <div className="dashboard-date-input">
          <label className="dashboard-form-label">End Time *</label>
          <input
            type="datetime-local"
            value={formData.endTime}
            onChange={(e) => setFormData({...formData, endTime: e.target.value})}
            required
            className="dashboard-form-input"
            min={formData.startTime || new Date().toISOString().slice(0, 16)}
          />
        </div>
      </div>
      
      {/* ✅ Email Notification Checkbox */}
      <div className="dashboard-notification-option">
        <label className="dashboard-notification-label">
          <input
            type="checkbox"
            checked={formData.notifyUsers}
            onChange={(e) => setFormData({...formData, notifyUsers: e.target.checked})}
            className="dashboard-notification-checkbox"
          />
          <span className="dashboard-notification-text">
            <span className="dashboard-button-icon">📧</span>
            Send email notification to all users
          </span>
        </label>
        <p className="dashboard-notification-help">
          All active users will receive an email about this maintenance schedule
        </p>
      </div>
      
      <div className="dashboard-form-actions">
        <button 
          type="submit" 
          disabled={submitting} 
          className="dashboard-submit-btn"
        >
          {submitting ? (
            <>
              <span className="dashboard-button-icon">⏳</span> Processing...
            </>
          ) : editingId ? (
            <>
              <span className="dashboard-button-icon">💾</span> Update Maintenance
            </>
          ) : (
            <>
              <span className="dashboard-button-icon">🚀</span> Schedule Maintenance
            </>
          )}
        </button>
        
        <button 
          type="button" 
          onClick={onCancel}
          className="dashboard-cancel-btn"
        >
          <span className="dashboard-button-icon">✕</span> Cancel
        </button>
      </div>
    </form>
  );
};

export default AdminDashboard;